import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Fade,
  Slide,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Cancel as CancelIcon,
  EventAvailable as EventIcon,
  AccessTime as TimeIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import rsvpService from '../services/rsvpService';
import eventService from '../services/eventService';
import { ListSkeleton } from '../components/common/LoadingSkeleton';

const MyRSVPs = () => {
  const navigate = useNavigate();
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRSVP, setSelectedRSVP] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  useEffect(() => {
    fetchRSVPs();
  }, [statusFilter]);

  const fetchRSVPs = async () => {
    try {
      setLoading(true);
      setError('');
      const filters = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await rsvpService.getUserRSVPs(filters);
      if (response.success) {
        setRsvps(response.data.rsvps);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch RSVPs');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setStatusFilter(newValue);
  };

  const handleOpenCancelDialog = (rsvp) => {
    setSelectedRSVP(rsvp);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedRSVP(null);
    setCancelReason('');
  };

  const handleCancelRSVP = async () => {
    if (!selectedRSVP) return;

    try {
      setCancelling(true);
      const response = await rsvpService.cancelRSVP(selectedRSVP._id, cancelReason);

      if (response.success) {
        // Update the RSVP list
        setRsvps(prevRsvps =>
          prevRsvps.map(rsvp =>
            rsvp._id === selectedRSVP._id
              ? { ...rsvp, status: 'cancelled' }
              : rsvp
          )
        );
        handleCloseCancelDialog();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel RSVP');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'EEEE, MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const getStatusColor = (status) => {
    return rsvpService.getStatusColor(status);
  };

  const canCancelRSVP = (rsvp) => {
    return rsvp.status === 'pending' || rsvp.status === 'confirmed';
  };

  const isUpcoming = (event) => {
    if (!event?.dates?.[0]?.startDate) return false;
    const eventDate = new Date(event.dates[0].startDate);
    return eventDate >= new Date();
  };

  const handleOpenQRDialog = async (rsvp) => {
    setSelectedRSVP(rsvp);
    setQrDialogOpen(true);
    setLoadingQR(true);

    try {
      const response = await rsvpService.getQRCode(rsvp._id);
      if (response.success) {
        setQrCode(response.data.qrCode);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load QR code');
      setQrDialogOpen(false);
    } finally {
      setLoadingQR(false);
    }
  };

  const handleCloseQRDialog = () => {
    setQrDialogOpen(false);
    setSelectedRSVP(null);
    setQrCode(null);
  };

  const canShowQR = (rsvp) => {
    return rsvp.status === 'confirmed' || rsvp.status === 'attended';
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Box mb={4}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              My Registrations
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage your event registrations
            </Typography>
          </Box>
          <ListSkeleton rows={5} />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box mb={4}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              My Registrations
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage your event registrations
            </Typography>
          </Box>
        </motion.div>

        {error && (
          <Fade in timeout={500}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </Fade>
        )}

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Paper
            sx={{
              mb: 3,
              transition: 'box-shadow 0.3s',
              '&:hover': {
                boxShadow: 3,
              }
            }}
          >
            <Tabs
              value={statusFilter}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="All" value="all" />
              <Tab label="Confirmed" value="confirmed" />
              <Tab label="Pending" value="pending" />
              <Tab label="Cancelled" value="cancelled" />
              <Tab label="Attended" value="attended" />
            </Tabs>
          </Paper>
        </motion.div>

        {/* RSVPs List */}
        {rsvps.length === 0 ? (
          <Fade in timeout={500}>
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="text.secondary">
                No registrations found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {statusFilter === 'all'
                  ? "You haven't registered for any events yet."
                  : `You don't have any ${statusFilter} registrations.`}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/events')}
              >
                Browse Events
              </Button>
            </Paper>
          </Fade>
        ) : (
          <Grid container spacing={3}>
            {rsvps.map((rsvp, index) => (
              <Grid item xs={12} key={rsvp._id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card
                    elevation={2}
                    sx={{
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                      }
                    }}
                  >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip
                            label={rsvpService.formatStatus(rsvp.status)}
                            color={getStatusColor(rsvp.status)}
                            size="small"
                          />
                          {isUpcoming(rsvp.event) && (
                            <Chip
                              label="Upcoming"
                              color="info"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        <Typography
                          variant="h5"
                          component="h2"
                          gutterBottom
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/events/${rsvp.event?.slug}`)}
                        >
                          {rsvp.event?.eventName || 'Event'}
                        </Typography>

                        <Grid container spacing={2} mt={1}>
                          <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CalendarIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {rsvp.event?.dates?.[0]?.startDate
                                  ? formatDate(rsvp.event.dates[0].startDate)
                                  : 'Date TBD'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <TimeIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {rsvp.event?.dates?.[0]?.startTime
                                  ? formatTime(rsvp.event.dates[0].startTime)
                                  : 'Time TBD'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {rsvp.event?.location?.venue || 'Location TBD'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {rsvp.additionalGuests > 0 && (
                          <Box display="flex" alignItems="center" gap={1} mt={2}>
                            <PeopleIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              +{rsvp.additionalGuests} additional {rsvp.additionalGuests === 1 ? 'guest' : 'guests'}
                            </Typography>
                          </Box>
                        )}

                        {rsvp.specialRequirements && (
                          <Box mt={2}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Special Requirements:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {rsvp.specialRequirements}
                            </Typography>
                          </Box>
                        )}

                        {rsvp.checkInTime && (
                          <Box mt={2}>
                            <Chip
                              label={`Checked in: ${format(new Date(rsvp.checkInTime), 'MMM dd, yyyy h:mm a')}`}
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        )}
                      </Box>

                      {rsvp.event?.banner?.url && (
                        <Box
                          component="img"
                          src={eventService.getImageUrl(rsvp.event.banner.url)}
                          alt={rsvp.event.eventName}
                          sx={{
                            width: 150,
                            height: 100,
                            objectFit: 'cover',
                            borderRadius: 1,
                            ml: 2,
                            display: { xs: 'none', sm: 'block' }
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>

                  <Divider />

                  <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        onClick={() => navigate(`/events/${rsvp.event?.slug}`)}
                      >
                        View Event
                      </Button>
                      {canShowQR(rsvp) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<QrCodeIcon />}
                          onClick={() => handleOpenQRDialog(rsvp)}
                        >
                          View QR Code
                        </Button>
                      )}
                    </Box>
                    {canCancelRSVP(rsvp) && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => handleOpenCancelDialog(rsvp)}
                      >
                        Cancel Registration
                      </Button>
                    )}
                  </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Cancel RSVP Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Cancel Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Are you sure you want to cancel your registration for{' '}
            <strong>{selectedRSVP?.event?.eventName}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for cancellation (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Let us know why you're cancelling..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseCancelDialog} disabled={cancelling}>
            Keep Registration
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelRSVP}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={handleCloseQRDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <QrCodeIcon />
            <Typography variant="h6" fontWeight={600}>Your QR Code</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={2}>
            {loadingQR ? (
              <CircularProgress size={60} />
            ) : qrCode ? (
              <>
                <Box
                  component="img"
                  src={qrCode}
                  alt="QR Code"
                  sx={{
                    width: 300,
                    height: 300,
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 3
                  }}
                />
                <Typography variant="h6" gutterBottom align="center">
                  {selectedRSVP?.event?.eventName}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  Show this QR code at the event entrance for check-in
                </Typography>
                <Alert severity="info" sx={{ width: '100%' }}>
                  Save this QR code to your device or take a screenshot for easier access
                </Alert>
              </>
            ) : (
              <Alert severity="error">Failed to load QR code</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseQRDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyRSVPs;
